import { NgSignaturePadOptions, SignaturePadComponent } from "@almothafar/angular-signature-pad";
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnInit, ViewChild } from "@angular/core";
import { PermitStep, PersonRole } from "@ha/data/basic";
import { BaseModalComponent } from "@ha/ui/common";
import { TranslocoService } from "@ngneat/transloco";
import { UntilDestroy } from "@ngneat/until-destroy";
import { Actions, Store } from "@ngxs/store";

import { validatePnr } from "@ha/util/common";
import { PermitDetailsActions } from "../../../permit-details/states/permit-details.action";
import { PermitDetailsSelectors } from "../../../permit-details/states/permit-details.selectors";
import { PermitTeamActions } from "../../../permit-team/states/permit-team.action";
import { PermitStepActions } from "../../../permit/states/permit-step.action";
import { PermitStepSelectors } from "../../../permit/states/permit-step.selectors";
import { PermitPerson } from "../../../shared/models/permit-person.model";

export interface SignPersonModalComponentData {
    permitId: number;
    roles: PersonRole[];
    person: PermitPerson;
    name: string;
}

@UntilDestroy()
@Component({
    templateUrl: "sign-person-modal.component.html",
    styleUrls: ["sign-person-modal.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SignPersonModalComponent extends BaseModalComponent implements OnInit, AfterViewInit {
    @ViewChild("signature")
    public signaturePad: SignaturePadComponent;

    public signaturePadOptions: NgSignaturePadOptions = undefined;

    public data: SignPersonModalComponentData;

    public hasSignature = false;

    public agreementAccepted = false;

    public PersonRole = PersonRole;

    public personalNumber = "";

    public hasForeignPersonalNumber = false;

    public isPersonalNumberValid = true;

    public get agreementAcceptedBefore() {
        return this.data.person.hasAcceptedUserAgreement;
    }

    public get isValid() {
        return this.hasSignature
            && ((this.data.person.concept != 2 && (this.agreementAcceptedBefore))
                || (this.data.person.concept == 2
                    && (this.data.person.personalNumber != "" || this.data.person.hasForeignPersonalNumber == true)
                    && (this.agreementAcceptedBefore)));
    }

    public get formattedRoles(): string {
        return this.data.roles.map((role) =>
            this.translocoService.translate("permit.signature.roles." + PersonRole[role].toLowerCase())
        ).join(", ");
    }

    public get endPostWatchSignature(): boolean {
        const permit = this.store.selectSnapshot(PermitDetailsSelectors.basicDetails);
        const step = this.store.selectSnapshot(PermitStepSelectors.currentStep);
        return permit?.endPostFireWatchSignatureRequired
            && (step?.step === PermitStep.started || step?.step === PermitStep.expiredNotSigned)
            && this.data.roles.includes(PersonRole.PostFireWatcher);
    }

    constructor(
        private store: Store,
        private actions$: Actions,
        private cdr: ChangeDetectorRef,
        private translocoService: TranslocoService,
    ) {
        super();
    }

    public ngOnInit(): void {
        this.store.dispatch(new PermitStepActions.GetCurrentStep(this.data.permitId));
        this.store.dispatch(new PermitDetailsActions.GetBasicDetails(this.data.permitId));
    }

    public ngAfterViewInit(): void {
        this.getScreenSize();
        this.signaturePad.clear(); // invoke functions from szimek/signature_pad API
    }

    public hasForeignPersonalNumberClick(): void {
        this.personalNumber = "";
    }

    public personalNumberOnChange(): void {
        this.hasForeignPersonalNumber = false;
    }

    @HostListener("window:resize", ["$event"])
    public getScreenSize(): void {
        let width = 400;
        if (window.innerWidth < width) {
            width = window.innerWidth;
        }

        this.signaturePadOptions = {
            minWidth: 1,
            maxWidth: 4,
            backgroundColor: "#ffffff",
            canvasWidth: width,
            canvasHeight: width / 2,
        };

        this.cdr.detectChanges();
    }

    public clear() {
        this.signaturePad.clear();
        this.hasSignature = !this.signaturePad.isEmpty();
    }

    public save() {
        if (this.personalNumber !== "") {
            this.isPersonalNumberValid = validatePnr(this.personalNumber);
        }

        if (this.isValid && this.isPersonalNumberValid === true) {
            this.store.dispatch(
                new PermitTeamActions.SignPermitWithSignature({
                    permitId: this.data.permitId,
                    personId: this.data.person.personId,
                    role: undefined,
                    roles: this.data.roles,
                    signatureData: this.signaturePad.toDataURL(),
                    endPostWatchSignature: this.endPostWatchSignature,
                    personalNumber: this.personalNumber,
                    hasForeignPersonalNumber: this.hasForeignPersonalNumber,
                    comment: undefined,
                }),
            );
            this.closeModal();
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public drawComplete(event: MouseEvent | Touch) {
        this.hasSignature = !this.signaturePad.isEmpty();
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
    public drawStart(event: MouseEvent | Touch) {
    }
}
